import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "react-router";
import { api } from "../../data";
import type { StaffWorkspace } from "../../app/session";
import { t } from "../../lib/sprache";
import { useTarifStand } from "../../components/Tarif";
import { useOnboardingPrefs } from "../creator/Einfuehrung";
import { useAuslassungTitel } from "../../lib/auslassung";
import { useRundgang } from "./coco-rundgang";
import { useNurLesen, useNurLesenPause } from "../../app/nurLesen";
import { loadLifecycle } from "./einstellungen/data";
import { date } from "../../lib/format";
import "./startleitfaden.css";

/**
 * Startleitfaden im Agentur-Portal (Inhaber 13.10.2026: „man versteht gar nicht, wo man anfangen soll“). Schritte in der
 * Reihenfolge des Coco-Rundgangs (K-557, 20.10.2026): Creator, Land, Firmendaten, Provision, Marke, Kooperation, Team. Die Übersicht zeigt die ganze
 * Liste, jede andere Seite ein schmales Banner mit dem nächsten offenen Schritt und dem Knopf direkt dorthin. Erledigt ist ein
 * Schritt, sobald es den Datensatz gibt; „Ausblenden“ gilt je Person (user_preferences.onboarding.guides.agentur_start_aus).
 */
export interface StartSchritt {
  key: "land" | "firma" | "konditionen" | "creator" | "marke" | "kooperation" | "team";
  done: boolean;
  label: string;
  hint: string;
  action: string;
  to: string;
}

const AUS_KEY = "agentur_start_aus";

interface Zaehler { talents: number; brands: number; deals: number; members: number; firmaOffen: boolean; konditionenOffen: boolean; landOffen: boolean }

async function ladeZaehler(orgId: string): Promise<Zaehler> {
  const eins = (table: string, limit = 1, ohneBeispiel = false) => api.select<{ id: string }>(table, { columns: "id", filters: [["org_id", "eq", orgId], ...(ohneBeispiel ? [["is_sample", "is", false] as const] : [])], limit }).then((r) => r.length).catch(() => 0);
  const [talents, brands, deals, members, invitations, setup, land] = await Promise.all([
    // 0325: Beispieldaten erledigen keinen Schritt
    eins("talents", 1, true), eins("brands", 1, true), eins("deals", 1, true), eins("memberships", 2), eins("invitations"),
    api.read<{ missing?: { step: string }[] }>("org_setup_status", { p_org: orgId }).catch(() => ({ missing: [] })),
    // 0266 (K-299): Land nie ausdrücklich gewählt (Altkonto aus dem Schnellstart vor 0265)
    api.select<{ country_confirmed_at: string | null }>("org_settings", { columns: "country_confirmed_at", filters: [["org_id", "eq", orgId]] })
      .then((r) => r.length > 0 && !r[0]!.country_confirmed_at).catch(() => false),
  ]);
  const missing = (typeof setup === "string" ? (JSON.parse(setup) as { missing?: { step: string }[] }) : setup)?.missing ?? [];
  return { talents, brands, deals, members: members + invitations, firmaOffen: missing.some((m) => m.step === "company" || m.step === "bank"), konditionenOffen: missing.some((m) => m.step === "terms" || m.step === "invoicing"), landOffen: land };
}

/** Schritte mit Stand; null, solange geladen wird oder die Person kein Admin ist */
export function useStartSchritte(ws: StaffWorkspace): { schritte: StartSchritt[]; erledigt: number; bereit: boolean; sichtbar: boolean; ausblenden: () => void } {
  const userId = api.session()?.userId ?? null;
  const { onboarding, ready, save } = useOnboardingPrefs(userId);
  const [weg, setWeg] = useState(false);
  const stand = useQuery({ queryKey: ["agentur-start", ws.org.org_id], enabled: ws.isChef, staleTime: 30_000, queryFn: () => ladeZaehler(ws.org.org_id) });
  const tarif = useTarifStand(ws.isChef ? ws.org.org_id : null);
  const z = stand.data;
  const einzelplatz = (tarif.data?.limits.max_seats ?? 0) === 1;
  const alle: StartSchritt[] = !z ? [] : [
    // K-557 (Inhaber 20.10.2026): zuerst die Creator, dann die eigenen Daten (wie der Coco-Rundgang); Creator anlegen braucht kein Land
    {
      key: "creator", done: z.talents > 0,
      label: t("Ersten Creator anlegen", "Add your first creator"),
      hint: t("Lege an, wen du managst. Die Einladung ins Portal ist freiwillig und geht auch später.", "Add who you manage. Inviting them to the portal is optional and can follow later."),
      action: t("Creator anlegen", "Add creator"), to: "/agentur/creator/einladen",
    },
    // 0266 (K-299): das Land vor Firmendaten und Kooperation, sonst schreibt die erste Kooperation still das falsche fest
    ...(z.landOffen ? [{
      key: "land" as const, done: false,
      label: ws.isCreator ? t("Bitte zuerst dein Land wählen", "Please choose your country first") : t("Bitte zuerst das Land deiner Agentur wählen", "Please choose your agency's country first"),
      hint: t("Danach richten sich Steuern, Rechnungen, Mahnungen, Verträge und Fristen; das Portal stellt alles selbst ein.", "Taxes, invoices, dunning, contracts and deadlines follow it; the portal sets everything up itself."),
      action: t("Land wählen", "Choose country"), to: "/agentur/einstellungen/firma",
    }] : []),
    {
      key: "firma", done: !z.firmaOffen,
      label: ws.isCreator ? t("Deine Angaben ergänzen", "Add your details") : t("Firmendaten ergänzen", "Add company details"),
      hint: t("Name, Anschrift und Steuernummer stehen auf deinen Verträgen und Rechnungen.", "Name, address and tax number appear on your contracts and invoices."),
      action: t("Zu den Firmendaten", "Open company details"), to: "/agentur/einstellungen/firma",
    },
    {
      key: "konditionen", done: !z.konditionenOffen,
      label: t("Provision und Zahlungsziel festlegen", "Set commission and payment terms"),
      hint: t("Dein üblicher Anteil und das Zahlungsziel gelten als Vorgabe für jede neue Kooperation.", "Your usual share and the payment terms are the default for every new collaboration."),
      action: t("Zur Provision", "Open commission"), to: "/agentur/einstellungen/provision",
    },
    {
      key: "marke", done: z.brands > 0,
      label: t("Erste Marke anlegen", "Add your first brand"),
      hint: t("Marken sind deine Kunden: Firma, Ansprechpartner und Rechnungsadresse. Am einfachsten legst du sie direkt in der Kooperation an; dann ist dieser Schritt mit erledigt.", "Brands are your customers: company, contact and billing address. The easiest way is to add them right in the collaboration; then this step is done too."),
      action: t("Marke anlegen", "Add brand"), to: "/agentur/marken?neu=1",
    },
    {
      key: "kooperation", done: z.deals > 0,
      label: ws.isCreator ? t("Ersten Deal anlegen", "Create your first deal") : t("Erste Kooperation anlegen", "Create your first collaboration"),
      hint: ws.isCreator
        ? t("Marke, Leistungen und Honorar an einem Ort. Daraus entstehen Vertrag und Rechnung.", "Brand, deliverables and fee in one place. Contract and invoice follow from it.")
        : t("Verbinde Creator und Marke: Leistungen, Honorar und Monate. Daraus entstehen Vertrag und Rechnung.", "Connect creator and brand: deliverables, fee and months. Contract and invoice follow from it."),
      action: ws.isCreator ? t("Deal anlegen", "Create deal") : t("Kooperation anlegen", "Create collaboration"), to: "/agentur/kooperationen/neu",
    },
    {
      key: "team", done: z.members > 1,
      label: t("Team einladen", "Invite your team"),
      hint: t("Hol deine Manager dazu und lege fest, wer was sehen und tun darf.", "Bring in your managers and decide who may see and do what."),
      action: t("Manager einladen", "Invite manager"), to: "/agentur/einstellungen/team",
    },
  ];
  // Creator-Konto: es gibt nur die Person selbst; Tarif mit einem Platz: kein Team
  const schritte = alle.filter((s) => (ws.isCreator ? s.key !== "creator" && s.key !== "team" && s.key !== "konditionen" : !(s.key === "team" && einzelplatz)));
  const erledigt = schritte.filter((s) => s.done).length;
  const aus = weg || !!(onboarding.guides as Record<string, boolean> | undefined)?.[AUS_KEY];
  return {
    schritte, erledigt, bereit: ws.isChef && !!z && schritte.length > 0,
    sichtbar: ws.isChef && ready && !!z && !aus && schritte.length > 0 && erledigt < schritte.length,
    ausblenden: () => { setWeg(true); void save({ guides: { [AUS_KEY]: true } as never }).catch(() => undefined); },
  };
}

/**
 * Übersicht einer frisch angelegten Agentur: Solange Pflichtangaben fehlen (zum Beispiel die Standardprovision), kann die
 * Übersicht nicht rechnen. Statt einer Sperrmeldung steht hier der Startleitfaden, immer und ohne „Ausblenden“.
 */
export function StartStattUebersicht({ ws, ausblendbar = false }: { ws: StaffWorkspace; ausblendbar?: boolean }) {
  // Befund 5b (21.10.2026): nur lesbar: keine Bitte um weitere Angaben
  const nurLesen = useNurLesen();
  // 0425 (S-6): in der Pause nicht „Willkommen“ und keine Einrichtung, die sich in der Pause nicht abschließen lässt
  const pauseBis = useNurLesenPause();
  return (
    <main id="main" className="content">
      <div className="pagehead"><div><div className="eyebrow">{ws.org.name}</div><h1>{nurLesen ? t("Übersicht", "Overview") : t("Willkommen bei CreatorOffice", "Welcome to CreatorOffice")}</h1>
        <p>{nurLesen && pauseBis
          ? t(`Pausiert bis ${pauseBis}. Danach richtest du die fehlenden Angaben in wenigen Minuten ein; Export und Abo & Tarif gehen weiter.`, `Paused until ${pauseBis}. Afterwards you add the missing details in a few minutes; export and Subscription & plan still work.`)
          : nurLesen
          ? t("Das Konto ist nur lesbar. Ansehen und Exportieren gehen weiter, mit einem Abo geht es sofort weiter.", "The account is read-only. Viewing and exporting still work, and with a subscription you carry on right away.")
          : t("Noch ein paar Angaben, dann zeigt dir diese Seite Umsatz, offene Rechnungen und was heute zu tun ist.", "A few more details, then this page shows your revenue, open invoices and what needs doing today.")}</p></div></div>
      <TestLaeuftZeile ws={ws} />
      <StartKarte ws={ws} immer={!ausblendbar} />
    </main>
  );
}

/** Ganze Liste auf der Übersicht */
export function StartKarte({ ws, immer = false }: { ws: StaffWorkspace; immer?: boolean }) {
  const { schritte, erledigt, sichtbar, bereit, ausblenden } = useStartSchritte(ws);
  const auslassung = useAuslassungTitel<HTMLElement>();
  // Befund 5b (21.10.2026): im Nur-Lesen-Zustand keine Startschritte
  const nurLesen = useNurLesen();
  if (nurLesen || (immer ? !bereit : !sichtbar)) return null;
  const naechster = schritte.find((s) => !s.done);
  return (
    <section ref={auslassung} className="panel stack tk-first-steps tk-start-karte" aria-labelledby="start-leitfaden" data-schreiben data-coco="start-liste">
      {/* K-573: ist die Liste zu hoch für Liste und Blase (Handy), hebt der Rundgang nur diesen Kopf hervor */}
      <div className="row between tk-start-kopfzeile" data-coco-kopf>
        <h2 id="start-leitfaden">{t("Deine ersten Schritte", "Your first steps")} <span className="tk-steps-stand">{t(`Einrichtung: ${erledigt} von ${schritte.length} erledigt`, `Setup: ${erledigt} of ${schritte.length} done`)}</span></h2>
        <div className="row">
          {/* Aktivierungsplan Vertrieb: der geführte Weg für Creator und Kooperation */}
          {!ws.isCreator && schritte.some((s) => (s.key === "creator" || s.key === "kooperation") && !s.done) && <Link className="btn" to="/agentur/schnellstart">{t("Schnellstart öffnen", "Open quick start")}</Link>}
          {!immer && <button type="button" className="quiet" onClick={ausblenden}>{t("Ausblenden", "Hide")}</button>}
        </div>
      </div>
      <p className="tk-ell" data-coco-kopf>{t("In dieser Reihenfolge ist dein Portal in wenigen Minuten arbeitsbereit. Jeder Knopf führt direkt an die richtige Stelle.", "In this order your portal is ready to work within minutes. Each button takes you straight to the right place.")}</p>
      <div className="progress" role="img" aria-label={t(`${erledigt} von ${schritte.length} Schritten erledigt`, `${erledigt} of ${schritte.length} steps done`)}>
        <span style={{ width: `${Math.round((erledigt / schritte.length) * 100)}%` }}></span>
      </div>
      <ol className="tk-steps">
        {schritte.map((s, i) => (
          <li key={s.key} className={s.done ? "done" : s === naechster ? "naechster" : undefined}>
            <span className="tk-step-mark" aria-hidden="true">{s.done ? "✓" : i + 1}</span>
            <span className="tk-step-text">
              <strong>{s.label}</strong>
              {s.done ? <span className="sr-only"> · {t("erledigt", "done")}</span> : <small className="tk-ell">{s.hint}</small>}
            </span>
            {/* D-2 (Inhaber 21.10.2026): nur der nächste offene Schritt trägt einen Knopf, die übrigen einen ruhigen Textlink */}
            {!s.done && (s === naechster
              ? <Link className="btn primary" to={s.to}>{s.action}</Link>
              : <Link className="tk-step-link" to={s.to}>{s.action} <span aria-hidden="true">→</span></Link>)}
          </li>
        ))}
      </ol>
    </section>
  );
}

/** Nächster offener Startschritt für die Leiste über der Seite; null, wo sie nichts zu sagen hat */
function useNaechsterStart(ws: StaffWorkspace) {
  const { pathname } = useLocation();
  const { schritte, erledigt, sichtbar, ausblenden } = useStartSchritte(ws);
  // K-573: während des Rundgangs mit Coco eingeklappt (Ruhe oben); danach wieder wie gewohnt
  const rundgang = useRundgang();
  if (!sichtbar || rundgang) return null;
  const naechster = schritte.find((s) => !s.done);
  if (!naechster) return null;
  const ziel = naechster.to.split("?")[0]!;
  // Auf der Übersicht steht die ganze Liste; auf der Zielseite selbst braucht es keinen Wegweiser
  if (pathname === "/agentur" || pathname.startsWith(ziel) || pathname.startsWith("/agentur/hilfe") || pathname.startsWith("/agentur/schnellstart")) return null;
  return { naechster, erledigt, gesamt: schritte.length, ausblenden };
}

/** Inhalt der Startleiste: Stand, nächster Schritt als Textlink, „Alle Schritte“ und Ausblenden (×) */
function StartInhalt({ x }: { x: NonNullable<ReturnType<typeof useNaechsterStart>> }) {
  return (
    <span className="tk-leiste-start" data-schreiben>
      <span className="tk-start-banner-stand">{t(`Einrichtung ${x.erledigt}/${x.gesamt}`, `Setup ${x.erledigt}/${x.gesamt}`)}</span>
      <span className="tk-leiste-start-text" title={x.naechster.hint}>{t("Als Nächstes: ", "Next: ")}<Link className="tk-leiste-link tk-leiste-link-stark" to={x.naechster.to}>{x.naechster.label}</Link></span>
      <Link className="tk-leiste-link tk-leiste-alle" to="/agentur">{t("Alle Schritte", "All steps")}</Link>
      <button type="button" className="tk-leiste-zu" onClick={x.ausblenden} aria-label={t("Startleitfaden ausblenden", "Hide the start guide")} title={t("Ausblenden", "Hide")}><span aria-hidden="true">×</span></button>
    </span>
  );
}

/** D-2 (Inhaber 21.10.2026): der nächste Startschritt als Teil der Testleiste (eine schmale Leiste statt zwei Banner) */
export function StartTeil({ ws }: { ws: StaffWorkspace }) {
  const x = useNaechsterStart(ws);
  return x ? <StartInhalt x={x} /> : null;
}

/** Schmale Leiste (höchstens 40 px) auf allen anderen Seiten, wenn keine Testleiste steht: der nächste offene Schritt */
export function StartBanner({ ws }: { ws: StaffWorkspace }) {
  const x = useNaechsterStart(ws);
  if (!x) return null;
  return (
    <div className="content tk-rahmen-hinweise tk-noprint">
      <div className="tk-leiste tk-start-banner" role="note"><StartInhalt x={x} /></div>
    </div>
  );
}

/**
 * Inhaber 21.10.2026: nach dem Abschluss mit Zahlungsart (und solange der Test läuft) auf der ersten Seite „Dein Test läuft bis …“,
 * nicht „Abo aktiv“. Übersicht für neue Konten und Schnellstart.
 */
export function TestLaeuftZeile({ ws }: { ws: StaffWorkspace }) {
  const leben = useQuery({ queryKey: ["org_lifecycle", ws.org.org_id], queryFn: () => loadLifecycle(api, ws.org.org_id), staleTime: 60_000, enabled: ws.isChef });
  const l = leben.data;
  if (!l || l.status !== "trial" || !l.trial_ends_at || l.read_only) return null;
  return <p className="notice success tk-test-laeuft" role="status">{t(`Dein Test läuft bis ${date(l.trial_ends_at, ws.org.timezone)}.`, `Your trial runs until ${date(l.trial_ends_at, ws.org.timezone)}.`)}</p>;
}
